<template>
  <BaseModal ref="modal" modal-classes="max-w-3xl mx-auto" @modal-after-leave="$emit('closed')">
    <template v-if="!internalProduct" #header>
      <div class="bg-white -mx-6 px-6 -my-4 py-4">
        <span class="block text-xs text-gray-700 uppercase tracking-wider">βημα 1</span>
        <h3 class="text-2xl text-gray-900">Επιλογή προϊόντος</h3>
      </div>
    </template>

    <template v-else #header>
      <div class="bg-white -mx-6 px-6 -my-4 py-4">
        <span class="block text-xs text-gray-700 uppercase tracking-wider">βημα 2</span>
        <h3 class="text-2xl text-gray-900">Παραμετροποίηση</h3>
      </div>
    </template>

    <template v-if="!internalProduct && !fetching">
      <div class="flex flex-col">
        <div class="flex-none">
          <label class="block font-medium uppercase text-sm text-muted mb-2">Search</label>
          <input
            type="text"
            class="input"
            :disabled="searching"
            @change="searchProduct($event.target.value)"
          />
        </div>
        <div class="flex-1 mt-8">
          <label class="block font-medium uppercase text-sm text-muted mb-2">Results</label>
          <div class="-mx-4">
            <BaseList nested>
              <BaseListBody>
                <template v-if="searching">
                  <BaseListItem>
                    <div class="text-center">
                      <Loader />
                    </div>
                  </BaseListItem>
                </template>
                <template v-else>
                  <BaseListItem v-for="product in products" :key="product.id">
                    <div class="flex -mx-4 items-baseline">
                      <div class="flex-1 px-4">
                        {{ product.name || 'unknown' }}
                      </div>
                      <div class="flex-none pl-4 pr-2">
                        <BaseLinkButton silver @click="customize(product)">
                          επιλογη
                        </BaseLinkButton>
                      </div>
                    </div>
                  </BaseListItem>
                </template>
              </BaseListBody>
            </BaseList>
          </div>
        </div>
      </div>
    </template>

    <template v-else-if="fetching">
      <div class="text-center">
        <Loader />
      </div>
    </template>

    <template v-else-if="product">
      <div class="bg-gray-300 -mx-6 -mt-6 p-6">
        <template v-if="internalProduct.type === 'package' && internalProduct.packageItems.length">
          <PackageCustomization :entity="internalProduct" @changed="packageItems = [...$event]" />
        </template>
        <template
          v-else-if="internalProduct.type === 'basic' && internalProduct.productServices.length"
        >
          <BasicProductCustomization :entity="internalProduct" v-model="productServices" />
        </template>
        <template v-else>
          <div class="bg-gray-100 p-4 rounded">
            <div class="flex -mx-4">
              <div class="flex-1 px-4">
                {{ internalProduct.name }}
              </div>
              <div class="flex-none px-4">{{ internalProduct.price }} &euro;</div>
            </div>
          </div>
        </template>
      </div>
      <div class="pt-6 flex items-baseline">
        <BaseLayoutSpaced horizontal snug>
          <BaseButton success @click="add"> Επιλογή </BaseButton>
          <BaseButton ghost @click="selectedProductId = null"> Πίσω </BaseButton>
        </BaseLayoutSpaced>
        <div v-if="totalPrice > 0" class="ml-auto pr-4">
          Σύνολο: <span class="font-medium">{{ totalPrice }}</span> &euro;
        </div>
      </div>
    </template>
  </BaseModal>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import orderLinesMixin from '@/mixins/orderLinesMixin';
import PackageCustomization from '@/components/Account/PackageCustomization.vue';
import BasicProductCustomization from '@/components/Account/BasicProductCustomization.vue';

export default {
  mixins: [orderLinesMixin],
  components: {
    PackageCustomization,
    BasicProductCustomization,
  },
  props: {
    baseId: {
      type: Number,
      default: 0,
    },
    options: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    searching: false,
    fetching: false,
    productIds: [],
    internalProduct: null,
    selectedProductId: null,
  }),
  computed: {
    ...mapState('entities', {
      products(state) {
        return this.productIds.map((id) => state.products.byId[id]).filter((x) => x);
      },
      product(state) {
        if (!this.selectedProductId) return null;

        return state.products.byId[this.selectedProductId] || null;
      },
    }),
    totalPrice() {
      if (!this.orderLines) return 0;

      return this.orderLines.reduce((acc, cur) => acc + cur.price, 0);
    },
  },
  watch: {
    product: {
      handler(newProduct) {
        if (!newProduct) {
          this.internalProduct = null;
          this.productServices.splice(0, this.productServices.length);
          this.clearOrderLines();
          return;
        }
        this.createOrderLineFromProduct(newProduct, this.baseId);
        this.internalProduct = JSON.parse(JSON.stringify(newProduct));
      },
      deep: true,
    },
    packageItems: {
      deep: true,
      handler() {
        this.createOrderLineFromPackage(this.internalProduct, this.baseId);
      },
    },
    productServices: {
      deep: true,
      handler() {
        this.createOrderLineFromProduct(this.internalProduct, this.baseId);
      },
    },
  },
  created() {
    this.searchProduct();
  },
  methods: {
    ...mapActions('ui/packages', ['fetchPackage']),

    ...mapActions('ui/basicProducts', ['fetchBasicProduct']),

    ...mapActions('ui/services', ['fetchService']),

    async searchProduct(name = '') {
      const { type } = this.options;
      const params = {
        filter: {
          name,
          ...(type ? { type } : {}),
          published: true,
          beta: false,
        },
      };

      this.searching = true;

      try {
        const { dispatch } = this.$store;

        const { productIds } = await dispatch('ui/products/fetchProductList', { params });

        this.productIds = [...productIds];
      } catch {
        // silence
      }
      this.searching = false;
    },
    async customize(product) {
      this.fetching = true;

      if (product.type === 'package') {
        const { packageId = null } = await this.fetchPackage({ packageId: product.id });

        this.selectedProductId = packageId;
      }

      if (product.type === 'basic') {
        const { basicProductId = null } = await this.fetchBasicProduct({
          basicProductId: product.id,
        });

        this.selectedProductId = basicProductId;
      }

      if (product.type === 'service') {
        const { serviceId = null } = await this.fetchService({ serviceId: product.id });

        this.selectedProductId = serviceId;
      }

      this.fetching = false;
    },
    add() {
      this.$emit('add', [...this.orderLines]);
      this.$refs.modal.doClose();
    },
  },
};
</script>
